/**
 * POST MULTIPLIERS (Nick, 2026-11-03) — "earn 3x the points for all the post benefits".
 *
 * A bounty whose step is a community post may carry a multiplier. The post that completes
 * it is boosted: for BOOST_DAYS from the payout, every upvote and comment payout
 * on it pays `multiplier` times. The bounty's own points are not multiplied — an admin sets
 * those directly — and the daily cap on payouts still counts events, so it still holds.
 *
 * Shared so the admin offers the field only where the server accepts it.
 */

/** Steps whose completing row is a community post the boost can attach to. */
export const BOOSTABLE_STEP_CODES: readonly string[] = ['communityPost'];

export const MULTIPLIER_MIN = 2;
export const MULTIPLIER_MAX = 5;

/** How long a boosted post earns at the multiplier, from the payout. */
export const BOOST_DAYS = 7;

export function isBoostableStep(stepCode: string | null | undefined): boolean {
  return BOOSTABLE_STEP_CODES.includes(stepCode ?? '');
}

/** Why this multiplier may not go on a bounty with this step, or null when it may. */
export function multiplierViolation(stepCode: string, multiplier: number | null | undefined): string | null {
  if (multiplier == null) return null;
  if (!isBoostableStep(stepCode)) return 'Only a community-post bounty can multiply its upvotes and comments.';
  if (!Number.isInteger(multiplier) || multiplier < MULTIPLIER_MIN || multiplier > MULTIPLIER_MAX) {
    return `A multiplier is a whole number from ${MULTIPLIER_MIN} to ${MULTIPLIER_MAX}.`;
  }
  return null;
}

/** The member-facing line, e.g. "3× points on its upvotes and comments for 7 days". */
export function multiplierLine(multiplier: number | null | undefined): string | null {
  if (multiplier == null || multiplier < MULTIPLIER_MIN) return null;
  return `${multiplier}× points on its upvotes and comments for ${BOOST_DAYS} days`;
}
